import * as React from 'react';

import classNames from 'classnames';

import type { CmlContent } from 'bundles/cml';

import _t from 'i18n!nls/discussions';

import 'css!./__styles__/NewThreadForm';

type ForumObject = {
  id: string;
  title: string;
  parentForumId?: string;
};

type ChangeEvent = {
  value: string | CmlContent | ForumObject;
  isValid: boolean;
};

const EMPTY_FORUM_ID = 'empty';

type ForumErrorKey = 'MISSING_FORUM' | null;

type ForumSelectorProps = {
  id?: string;
  rootForumId?: string;
  handleInputChange: (type: string, event: ChangeEvent) => void;
  forums: Array<ForumObject>;
  value: ForumObject;
  hasError?: boolean;
  elemRefHook?: (elem: HTMLElement | null) => void;
};

const ForumSelector: React.FC<ForumSelectorProps> = ({
  id,
  rootForumId,
  handleInputChange,
  forums,
  value,
  hasError,
  elemRefHook,
}) => {
  const checkForErrors = (newValue: string): ForumErrorKey => {
    if (newValue === EMPTY_FORUM_ID || !newValue) {
      return 'MISSING_FORUM';
    }
    return null;
  };

  const validate = (newValue: string) => checkForErrors(newValue) === null;

  const getErrorMessage = (key: ForumErrorKey) => {
    const translatedStrings: Record<string, string> = {
      MISSING_FORUM: _t('Error: Please select a forum'),
    };
    return key ? translatedStrings[key] : '';
  };

  const renderError = (errorMessage?: string) => (
    <div className="c-form-error-message color-danger" role="alert">
      {errorMessage || getErrorMessage(checkForErrors(value?.id))}
    </div>
  );

  const handleInputChangeInternal = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedForumId = e.currentTarget.value;
    const event = {
      value: forums.find((item) => item.id === selectedForumId) || {
        id: EMPTY_FORUM_ID,
        title: _t('Please select a forum'),
      },
      isValid: validate(selectedForumId),
    };
    handleInputChange('currentForum', event);
  };

  const selectorItems = forums.map((forum: ForumObject) => (
    <option key={forum.id} value={forum.id}>
      {forum.parentForumId && forum.parentForumId !== rootForumId && '\x20\u2515\x20\x20'}
      {forum.title}
    </option>
  ));

  const emptyForumOption = (
    <option key={EMPTY_FORUM_ID} value={EMPTY_FORUM_ID}>
      {_t('Please select a forum')}
    </option>
  );

  const classes = classNames({
    'c-form-input': true,
    'c-form-select': true,
    'c-has-error': hasError,
  });

  return (
    <div className="c-form">
      <select
        id={id}
        value={value?.id || EMPTY_FORUM_ID}
        ref={elemRefHook}
        className={classes}
        aria-label={_t('Forum')}
        onChange={handleInputChangeInternal}
        aria-required={true}
        aria-invalid={hasError}
        required
      >
        {(!value || value.id === EMPTY_FORUM_ID) && emptyForumOption}
        {selectorItems}
      </select>
      {hasError && renderError()}
    </div>
  );
};

const MemoizedForumSelector = React.memo(ForumSelector);

export { MemoizedForumSelector as ForumSelector };
